import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../i18n';
import { toPoints } from '../../utils/energyPalette';
import {
    barHeightPct,
    barLayout,
    formatAxisValue,
    labelIndices,
    labelStride,
    niceTicks,
    scrollTargetFor,
} from '../../utils/dailyEnergyChart';

// ─────────────────────────────────────────────────────────────────────────
// DailyEnergyChart (#385) — one bar per day of the selected range, between the
// cycle picker and the pizza.
//
// Hand-rolled, like the pizza: this repo ships no charting library and one is
// not worth adding for a bar per day. Unlike the pizza the bars are HTML
// <button>s rather than SVG shapes — they are the primary way into a day, so
// they get native focus, Enter/Space and disabled semantics for free instead of
// re-implementing them on a <rect>.
//
// SINGLE-COLOUR bars, deliberately, with the per-pillar breakdown one click
// away in the day view. Stacking by pillar was considered and rejected: two of
// the five locked pillar colours (Writing #374151, Listening #1E3A8A) sit at
// ~1.7:1 against this dark glass, so a day spent mostly writing would read as a
// missing bar, and at 67+ days a 2–4px bar cannot carry five legible segments.
//
// Geometry lives in utils/dailyEnergyChart.js (unit-tested). This component
// measures its own row — container-driven, never a viewport breakpoint, because
// the panel is routinely a ~380px docked split on a wide screen (#142/#243) —
// and scrolls horizontally INSIDE its own box once bars hit their minimum width.
// ─────────────────────────────────────────────────────────────────────────

const AXIS_W = 40;
const PLOT_H = 128;
const LABEL_H = 20;
/** jsdom and the very first paint have no width yet; draw something sane. */
const FALLBACK_W = 480;

const DailyEnergyChart = ({ data, loading = false, error = null, selectedDate = null, onSelectDay }) => {
    const { t, language } = useLanguage();

    // Callback ref: the measured row only exists once data has arrived, so a
    // mount-time effect would find null and never look again.
    const [rowEl, setRowEl] = useState(null);
    const [rowWidth, setRowWidth] = useState(0);
    useEffect(() => {
        if (!rowEl) return undefined;
        const measure = () => setRowWidth(rowEl.getBoundingClientRect().width || 0);
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(measure);
        ro.observe(rowEl);
        return () => ro.disconnect();
    }, [rowEl]);

    const days = useMemo(() => data?.days || [], [data]);
    const today = data?.today || null;

    const values = useMemo(
        () => days.map((d) => (d?.future ? 0 : Math.max(0, toPoints(d?.total_pts)))),
        [days],
    );
    const peak = values.reduce((m, v) => (v > m ? v : m), 0);
    const { ticks, max: scaleMax } = useMemo(() => niceTicks(peak), [peak]);

    const available = Math.max(0, (rowWidth || FALLBACK_W) - AXIS_W);
    const layout = barLayout(days.length, available);
    const stride = labelStride(layout.slot);
    const labelled = useMemo(() => new Set(labelIndices(days.length, stride)), [days.length, stride]);

    const todayIndex = today ? days.findIndex((d) => d.date === today) : -1;
    const selectedIndex = selectedDate ? days.findIndex((d) => d.date === selectedDate) : -1;

    // Roving tabindex: ONE bar is in the tab order and the arrow keys move
    // between them, so a 367-day range is not 367 tab stops.
    const defaultFocus = useMemo(() => {
        if (selectedIndex >= 0) return selectedIndex;
        if (todayIndex >= 0) return todayIndex;
        for (let i = days.length - 1; i >= 0; i -= 1) if (!days[i]?.future) return i;
        return 0;
    }, [days, selectedIndex, todayIndex]);
    const [focusIndex, setFocusIndex] = useState(defaultFocus);
    useEffect(() => { setFocusIndex(defaultFocus); }, [defaultFocus]);

    const [activeIndex, setActiveIndex] = useState(-1);
    const buttonsRef = useRef([]);
    const scrollerRef = useRef(null);

    // Bring the day that matters (selected, else today, else the newest) into
    // view whenever a scrolling range loads. Layout effect so the user never
    // sees the chart jump from the oldest end.
    useLayoutEffect(() => {
        const el = scrollerRef.current;
        if (!el || !layout.scrolls) return;
        el.scrollLeft = scrollTargetFor(defaultFocus, layout.slot, layout.plotWidth, el.clientWidth);
        // Deliberately keyed on the DATA and on the scroll mode only: re-centring
        // on every resize would fight a user who has scrolled to look back.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, layout.scrolls]);

    const dayFmt = useMemo(() => {
        try {
            return new Intl.DateTimeFormat(language === 'es' ? 'es' : 'en', {
                month: 'short', day: 'numeric', timeZone: 'UTC',
            });
        } catch {
            return null;
        }
    }, [language]);
    const longFmt = useMemo(() => {
        try {
            return new Intl.DateTimeFormat(language === 'es' ? 'es' : 'en', {
                weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
            });
        } catch {
            return null;
        }
    }, [language]);
    const fmt = (f, iso) => {
        if (!f || !iso) return iso || '';
        const d = new Date(`${iso}T00:00:00Z`);
        return Number.isNaN(d.getTime()) ? iso : f.format(d);
    };

    const pts = t('energyPtsSuffix', 'pts');

    const barLabel = (d, i) => {
        if (d.future) return t('energyDailyFutureLabel', '{date}: not yet', { date: fmt(longFmt, d.date) });
        const base = t('energyDailyBarLabel', '{date}: {pts} points', { date: fmt(longFmt, d.date), pts: d.total_pts });
        return i === todayIndex ? `${base} (${t('energyDailyToday', 'Today')})` : base;
    };

    const moveFocus = useCallback((next) => {
        const n = Math.max(0, Math.min(days.length - 1, next));
        setFocusIndex(n);
        buttonsRef.current[n]?.focus();
    }, [days.length]);

    const onKeyDown = (e, i) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); moveFocus(i + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); moveFocus(i - 1); }
        else if (e.key === 'Home') { e.preventDefault(); moveFocus(0); }
        else if (e.key === 'End') { e.preventDefault(); moveFocus(days.length - 1); }
    };

    const active = activeIndex >= 0 ? days[activeIndex] : null;
    const anySpend = values.some((v) => v > 0);

    return (
        <section
            data-testid="energy-daily-chart"
            data-days={days.length}
            data-scrolls={layout.scrolls ? 'true' : 'false'}
            data-loading={loading ? 'true' : 'false'}
            aria-busy={loading ? 'true' : 'false'}
            aria-label={t('energyDailyTitle', 'Daily energy')}
            className="pai-card px-6 py-5 min-w-0 max-w-full"
        >
            <header className="flex items-center justify-between gap-3 min-w-0">
                <span className="pai-eyebrow truncate min-w-0">{t('energyDailyTitle', 'Daily energy')}</span>
                {data && !error && (
                    <span
                        data-testid="energy-daily-readout"
                        aria-live="polite"
                        className="text-[12.5px] text-pai-meta tnum truncate min-w-0 text-right"
                    >
                        {active
                            ? (active.future
                                ? `${fmt(longFmt, active.date)} · ${t('energyDailyNotYet', 'not yet')}`
                                : `${fmt(longFmt, active.date)} · ${active.total_pts} ${pts}`)
                            : `${t('energyDailyRangeTotal', 'Range total')}: ${data.total_pts ?? '0.00'} ${pts}`}
                    </span>
                )}
            </header>

            {loading && !data && (
                <div className="flex items-center gap-2 py-6 text-[13px] text-pai-meta">
                    <Loader2 size={14} className="animate-spin" />
                    {t('energyLoading', 'Loading…')}
                </div>
            )}

            {error && (
                <div data-testid="energy-daily-error" className="py-4 text-[13px] text-pai-red break-words">
                    {t('energyDailyError', "Couldn't load your daily energy.")}
                </div>
            )}

            {data && !error && (
                <>
                    {!anySpend && (
                        <p data-testid="energy-daily-empty" className="mt-2 text-[12.5px] text-pai-meta break-words">
                            {t('energyDailyEmpty', 'No energy was used in this range.')}
                        </p>
                    )}

                    <div
                        ref={setRowEl}
                        // A refetch keeps the previous bars on screen (no layout
                        // jump) but dims them, so stale days never read as current.
                        className={`mt-3 flex min-w-0 max-w-full transition-opacity ${loading ? 'opacity-50' : ''}`}
                    >
                        {/* Y axis — outside the scroller, so it stays put while
                            a long range scrolls under it. */}
                        <div
                            aria-hidden="true"
                            className="relative flex-shrink-0"
                            style={{ width: AXIS_W, height: PLOT_H }}
                        >
                            {ticks.map((v) => (
                                <span
                                    key={v}
                                    className="absolute right-2 text-[10.5px] leading-none text-pai-faint tnum"
                                    style={{ bottom: `calc(${barHeightPct(v, scaleMax) || 0}% - 5px)` }}
                                >
                                    {formatAxisValue(v)}
                                </span>
                            ))}
                        </div>

                        <div
                            ref={scrollerRef}
                            data-testid="energy-daily-scroller"
                            className="relative flex-1 min-w-0 overflow-x-auto overflow-y-hidden"
                        >
                            <div className="relative" style={{ width: layout.plotWidth }}>
                                <div className="relative" style={{ height: PLOT_H }}>
                                    {ticks.map((v) => (
                                        <span
                                            key={v}
                                            aria-hidden="true"
                                            className={`absolute left-0 right-0 border-t ${v === 0 ? 'border-pai' : 'border-pai-hairline'}`}
                                            style={{ bottom: `${barHeightPct(v, scaleMax)}%` }}
                                        />
                                    ))}
                                    <div className="absolute inset-0 flex items-end">
                                        {days.map((d, i) => {
                                            const isToday = i === todayIndex;
                                            const isSelected = i === selectedIndex;
                                            const h = barHeightPct(values[i], scaleMax);
                                            return (
                                                <button
                                                    key={d.date}
                                                    ref={(el) => { buttonsRef.current[i] = el; }}
                                                    type="button"
                                                    data-testid="energy-daily-bar"
                                                    data-date={d.date}
                                                    {...(d.future ? {} : { 'data-total': d.total_pts })}
                                                    data-future={d.future ? 'true' : 'false'}
                                                    data-today={isToday ? 'true' : 'false'}
                                                    data-selected={isSelected ? 'true' : 'false'}
                                                    disabled={d.future}
                                                    tabIndex={i === focusIndex ? 0 : -1}
                                                    aria-label={barLabel(d, i)}
                                                    aria-pressed={d.future ? undefined : isSelected}
                                                    title={barLabel(d, i)}
                                                    onClick={() => !d.future && onSelectDay?.(d.date)}
                                                    onKeyDown={(e) => onKeyDown(e, i)}
                                                    onFocus={() => { setFocusIndex(i); setActiveIndex(i); }}
                                                    onBlur={() => setActiveIndex(-1)}
                                                    onMouseEnter={() => setActiveIndex(i)}
                                                    onMouseLeave={() => setActiveIndex(-1)}
                                                    className="pai-no-hit-slop group relative h-full flex items-end justify-center flex-shrink-0 rounded-[3px] focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-white/80 disabled:cursor-default"
                                                    style={{ width: layout.slot }}
                                                >
                                                    {d.future ? (
                                                        // An empty SLOT — the day exists in the cycle,
                                                        // nothing has happened on it yet.
                                                        <span
                                                            aria-hidden="true"
                                                            className="block h-full rounded-[3px] bg-white/[0.025]"
                                                            style={{ width: layout.barWidth }}
                                                        />
                                                    ) : (
                                                        <span
                                                            aria-hidden="true"
                                                            className={`block rounded-t-[3px] transition-colors ${
                                                                isSelected
                                                                    ? 'bg-white shadow-glow-sm'
                                                                    : 'bg-white/[0.55] group-hover:bg-white group-focus-visible:bg-white'
                                                            }`}
                                                            // A zero day keeps a 1px stub: it still
                                                            // opens, and it reads as "nothing", not
                                                            // as "no data".
                                                            style={{ width: layout.barWidth, height: h > 0 ? `${h}%` : 1 }}
                                                        />
                                                    )}
                                                    {isToday && (
                                                        <span
                                                            aria-hidden="true"
                                                            data-testid="energy-daily-today"
                                                            className="absolute -bottom-[7px] left-1/2 -translate-x-1/2 w-[5px] h-[5px] rounded-full bg-pai-amber"
                                                        />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* X axis. Thinned to one label per `stride` days so
                                    neighbours never collide; each label is
                                    left-aligned in its own run of slots, so none
                                    can spill off the left edge. */}
                                <div aria-hidden="true" className="relative overflow-hidden" style={{ height: LABEL_H }}>
                                    {days.map((d, i) => (labelled.has(i) ? (
                                        <span
                                            key={d.date}
                                            data-testid="energy-daily-label"
                                            className={`absolute top-[8px] text-[10.5px] leading-none whitespace-nowrap overflow-hidden tnum ${
                                                i === todayIndex ? 'text-pai-amber font-semibold' : 'text-pai-faint'
                                            }`}
                                            style={{ left: i * layout.slot, width: stride * layout.slot }}
                                        >
                                            {fmt(dayFmt, d.date)}
                                        </span>
                                    ) : null))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

export default DailyEnergyChart;
